import {
  GoAButton,
  GoAButtonGroup,
  GoACallout,
  GoAFormItem,
  GoAInputText,
  GoAModal,
  GoASpacer,
  GoATextarea,
} from '@abgov/react-components';
import { useEffect, useRef, useState } from 'react';
import { DamageCardComponent } from './damage-card';
import {
  DamageSketch,
  DesktopDamageSelectorLayoutConfiguration,
  MobileDamageSelectorLayoutConfiguration,
} from '../sketches/damage';
import p5 from 'p5';

import { InitialPointOfImpact } from '../../models/initial-point-of-impact';

import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { WizardButtonGroup } from '../wizard/wizard-button-group-component';
import { updateCollision } from '../../state/slices/collisionSlice';
import { Collision, DamageSelection } from '../../models/collision';
import { useIsDesktop } from '../../utils/isDesktop';
import {
  hasLoad,
  isAttachment,
  isSpilled,
  setReviewDamageModified,
} from '../../state/slices/configurationSlice';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  getReturnToValue,
  IsInReviewEditMode,
} from '../wizard/wizard-component';
import {
  pushNotification,
  removeAllNotifications,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';

export interface DamageStickerProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const DamageStickerComponent = ({
  navigationConfiguration,
}: DamageStickerProps) => {
  const maxCharacterCount = 140;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const [nextPage, setNextPage] = useState('');

  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );
  const p5Ref = useRef<HTMLDivElement>(null);
  const containerDiv = useRef<HTMLDivElement>(null);

  const [reviewDamageModified, setReviewDamageModified] = useState(
    stateConfiguration.uiTriggers.reviewDamageModified,
  );

  const [reviewPointOfImpactModified, setReviewPointOfImpactModified] =
    useState(stateConfiguration.uiTriggers.reviewPointOfImpactModified);

  const [
    initialPointOfImpactLocationList,
    setInitialPointOfImpactLocationList,
  ] = useState<InitialPointOfImpact[]>([]);

  const [showReturnToReviewModal, setShowReturnToReviewModal] = useState(false);

  const [
    releventPointOfImpactLocationList,
    setReleventPointOfImpactLocationList,
  ] = useState<InitialPointOfImpact[]>([]);

  const [damageAdditionalDetails, setDamageAdditionalDetails] = useState(
    stateCollision.collision.vehicles![0].damage?.additionalDamageDetails,
  );

  const [damageSketch, setDamageSketch] = useState<DamageSketch>(
    new DamageSketch(),
  );

  const [damageSelectionsList, setDamageSelectionsList] = useState<
    DamageSelection[]
  >(stateCollision.collision.vehicles![0].damage?.damageSelection!);

  useEffect(() => {
    if (
      (IsInReviewEditMode() &&
        stateConfiguration.uiTriggers.reviewDamageModified) ||
      stateConfiguration.uiTriggers.reviewPointOfImpactModified
    ) {
      dispatch(removeAllNotifications());
      dispatch(
        pushNotification({
          body: 'Your changes have been saved. Review your updated digital damage sticker below. Click "Save and continue" to update your vehicle damage photos where applicable',
          notificationType: NotificationType.importantWarning,
        }),
      );
    }
  }, [stateConfiguration.uiTriggers.reviewDamageModified]);

  useEffect(() => {
    const pois: InitialPointOfImpact[] = [];

    stateConfiguration.configuration.pointOfImpact.forEach((poi: any) => {
      pois.push(new InitialPointOfImpact(poi.id, poi.value));
    });

    setInitialPointOfImpactLocationList(pois);
  }, [stateConfiguration.configuration.pointOfImpact]);

  useEffect(() => {
    const damageSelectionsListValues = damageSelectionsList.map((item) =>
      item.pointOfImpactId?.toString(),
    );

    const releventDamageLocations = initialPointOfImpactLocationList.filter(
      (item) => damageSelectionsListValues.includes(item.id.toString()),
    );

    setReleventPointOfImpactLocationList(releventDamageLocations);
  }, [initialPointOfImpactLocationList, damageSelectionsList]);

  useEffect(() => {
    let pageConfig = '';

    if (IsInReviewEditMode()) {
      pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.manager !== undefined,
      )!.manager;
    } else {
      pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.default !== undefined,
      )!.default;
    }

    setNextPage(pageConfig ?? 'error');
  }, []);

  useEffect(() => {
    if (p5Ref.current) {
      let startCount = 1;

      const p5Instance = new p5(
        damageSketch.sketch(
          containerDiv.current!,
          damageSelectionsList,
          releventPointOfImpactLocationList,
          startCount,
          handleDamageListUpdate,
          false,
          isDesktop
            ? new DesktopDamageSelectorLayoutConfiguration(
                releventPointOfImpactLocationList,
              )
            : new MobileDamageSelectorLayoutConfiguration(
                releventPointOfImpactLocationList,
              ),
          true,
        ),
        p5Ref.current,
      );

      return () => {
        p5Instance.remove();
      };
    }
  }, [releventPointOfImpactLocationList]);

  const handleNavigate = () => {
    if (
      IsInReviewEditMode() &&
      !reviewDamageModified &&
      !reviewPointOfImpactModified
    ) {
      setShowReturnToReviewModal(true);
    } else if (IsInReviewEditMode() && reviewDamageModified) {
      // we need to handle going to the media page explicitly here
      const pageConfig = navigationConfiguration.pagingConfiguration.find(
        (item) => item.manager !== undefined,
      )!.manager;
      window.location.hash = pageConfig + '?returnTo=review';
    } else {
      // normal flow
      navigationConfiguration.navigate(nextPage);
    }
  };

  const validate = (): boolean => {
    if (
      damageAdditionalDetails &&
      damageAdditionalDetails.length > maxCharacterCount
    ) {
      return false;
    }

    return true;
  };

  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        navigationConfiguration.stageCompleted
          ? navigationConfiguration.stageCompleted
          : false,
        !navigationConfiguration.stageCompleted,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    updatedCollision.vehicles = [
      {
        ...stateCollision.collision.vehicles![0],
        damage: {
          ...stateCollision.collision.vehicles![0].damage,
          additionalDamageDetails: damageAdditionalDetails,
          damageSelection: [...damageSelectionsList],
        },
      },
    ];

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  const handleDamageListUpdate = (newDamageSelection: {
    pointOfImpactId: string;
    value: boolean;
    isInitialPointOfImpact: boolean;
    clickTime: number;
  }) => {
    setDamageSelectionsList((prevList) => {
      const itemExists = prevList.some(
        (item) =>
          item.pointOfImpactId?.toString() ===
          newDamageSelection.pointOfImpactId?.toString(),
      );

      // If the item exists, use map to return the updated array
      if (itemExists) {
        return prevList.map((item) =>
          item.pointOfImpactId?.toString() ===
          newDamageSelection.pointOfImpactId?.toString()
            ? newDamageSelection
            : item,
        );
      }

      // If the item does not exist, add the new item
      return [...prevList, newDamageSelection];
    });
  };

  return (
    <div>
      <GoAModal
        open={showReturnToReviewModal}
        actions={
          <GoAButtonGroup alignment="end">
            <GoAButton
              type="tertiary"
              onClick={() => {
                setShowReturnToReviewModal(false);
              }}
            >
              Cancel
            </GoAButton>
            <GoAButton
              type="primary"
              onClick={() => {
                setShowReturnToReviewModal(false);
                save();
                navigationConfiguration.navigate('#review');
              }}
            >
              View report summary
            </GoAButton>
          </GoAButtonGroup>
        }
      >
        <div>
          Click 'View report summary' to return to the report review page.
        </div>
      </GoAModal>
      <div>
        <h1>Vehicle damage</h1>
        <p>
          Please provide the required information about the damage to your
          vehicle.
        </p>
        <h2>Damage Sticker</h2>
        <p>
          Review and verify the damages you've indicated. This will reflect on
          your digital damage sticker which will be available once your report
          is approved by law enforcement.
        </p>
        <GoASpacer vSpacing={'l'}></GoASpacer>
        <GoACallout heading="Important" type="important" size="medium">
          Your collision report will require review and approval by law
          enforcement upon submission. Once approved, your digital damage
          sticker will be available on your dashboard.
        </GoACallout>
        <div ref={containerDiv} className="damage-vehicle">
          <div ref={p5Ref} />
        </div>
        <GoASpacer vSpacing={'2xs'} />
        <h3>First point of damage</h3>
        <GoASpacer vSpacing={'m'} />
        <strong style={{ paddingLeft: '10px' }}>
          {damageSelectionsList.findIndex((x) => {
            return x.isInitialPointOfImpact;
          })! + 1}
          {'. '}
          {
            stateConfiguration.configuration.pointOfImpact.find((x: any) => {
              return (
                x.id ===
                parseInt(
                  damageSelectionsList.find((y) => y.isInitialPointOfImpact)
                    ?.pointOfImpactId!,
                )
              );
            })!.formLabel
          }
        </strong>
        <GoASpacer vSpacing={'2xl'} />

        <div>
          <GoAFormItem
            label="Additional Damage Details"
            requirement="optional"
            helpText="Add more details about the damage to your vehicle, if any."
          >
            <GoATextarea
              value={damageAdditionalDetails}
              name="item"
              onChange={(item, value) => {
                setDamageAdditionalDetails(value);
              }}
              countBy="character"
              maxCount={maxCharacterCount}
              width={isDesktop ? '370px' : '100%'}
            ></GoATextarea>
          </GoAFormItem>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: isDesktop ? 'row' : 'column',
            gap: '10px',
          }}
        >
          <div>
            <WizardButtonGroup
              nextPage={nextPage}
              save={save}
              validate={validate}
              navigate={handleNavigate}
              showViewSummaryButton={false}
              customButton={
                !IsInReviewEditMode() ? (
                  <GoAButton
                    type="tertiary"
                    onClick={() => {
                      navigationConfiguration.navigate('#damage');
                    }}
                  >
                    Edit my damages
                  </GoAButton>
                ) : (
                  ''
                )
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
